import type { InputT } from "@patient-watch/types";
import { PlusCircle, Trash2 } from "lucide-react";
import React, { useEffect } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useSelectInputsByIds } from "../api/selectInputsByIds";
import { Button } from "@patient-watch/ui/button";
import { Label } from "@patient-watch/ui/label";
import RepeatableGroupEntry from "./RepeatableGroupEntry";

interface RepeatableGroupProps {
    name: string;
    label: string;
    description?: string;
    inputList?: string[];
    templateInputs?: InputT[];
    required?: boolean;
}

const RepeatableGroup = ({
    name,
    label,
    description,
    inputList = [],
    templateInputs: _templateInputs = [],
    required = false,
}: RepeatableGroupProps) => {
    const { data: inputs } = useSelectInputsByIds(inputList);
    const { control } = useFormContext();

    const { fields, append, remove } = useFieldArray({
        control,
        name: name,
    });

    // Create a new item with the correct structure
    const createNewItem = () => {
        return {
            values: inputList.map((inputId) => ({
                [inputId]: "",
            })),
        };
    };

    useEffect(() => {
        if (fields.length === 0) {
            append(createNewItem());
        }
    }, []);

    return (
        <div className="space-y-4">
            <div>
                <Label className="font-semibold text-base">
                    {label}
                    {required && <span className="ml-0.5 text-red-500">*</span>}
                </Label>
                {description && <p className="mt-1 text-muted-foreground text-sm">{description}</p>}
            </div>

            <div className="space-y-4">
                {fields.map((item, index) => {
                    return (
                        <div key={item.id} className="p-4 border rounded-md">
                            <div className="flex justify-between items-center mb-3">
                                <Label className="font-medium text-sm">Item {index + 1}</Label>
                                {fields.length > 1 && (
                                    <Button type="button" variant="ghost" size="sm" onClick={() => remove(index)}>
                                        <Trash2 className="w-4 h-4 text-destructive" />
                                        <span className="sr-only">Remove item</span>
                                    </Button>
                                )}
                            </div>

                            <RepeatableGroupEntry
                                repeatableGroupInputId={name}
                                groupIndex={index}
                                control={control}
                                inputs={inputs || []}
                            />
                        </div>
                    );
                })}
            </div>

            <div className="flex gap-2">
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                        append(createNewItem());
                    }}
                >
                    <PlusCircle className="mr-2 w-4 h-4" />
                    Add Item
                </Button>
            </div>
        </div>
    );
};

export default RepeatableGroup;
